/**
 * @lines98/client/lib/gnome-reveal — переиспользуемый ПЛЕЕР reveal-анимации открытия
 * гнома (новый тонкий модуль GN-2b, O-8). «In-card силуэт → проявление»: scale-вспышка
 * слота-целиком от `transform-origin:center` + снятие силуэт-фильтра, затем подмена на
 * открытую карточку. Очередь при нескольких сразу (Promise-цепочка).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (code-structure §1/§3 «общее поведение = модуль, не код в экране»;
 * owner-directive 2026-05-30; тикет GN-2b AC-8). Reveal-логику нельзя инлайнить в
 * `gnomes-screen` — она переиспользуема (экран только вычисляет «что открылось» через
 * `newlyUnlockedColors` и делегирует сюда). Экран НЕ содержит scale/transition-кода —
 * `frontend-code-reviewer` блокирует дубль (AC-8). Один плеер → один источник тайминга/
 * фаз; усложнится анимация — меняем здесь, не по экранам.
 *
 * **reduce-motion через DI (`opts.reduceMotion`), не чтение matchMedia внутри** (AC-16):
 * логика очереди/мгновенного финала юнит-тестируема без DOM (как `reward-block`
 * `prefersReducedMotion`-флаг). При reduce → каждый слот мгновенно переводится в финал
 * (onSettled) БЕЗ таймеров и без задержек очереди (Promise резолвится в тот же микротик).
 *
 * **§8 trace (НЕ magic-числа):** длительность вспышки `REVEAL_FLASH_MS` = 180ms — из
 * §8-диапазона морфа карточки (150–200ms ease-out; тот же порядок, что `transition:
 * height 140ms ease-out` hover-морфа в gnome-card.css). Сама scale-кривая
 * (`scale(1)→1.6→1`, `transform-origin:center`, GPU-only opacity+transform) живёт в CSS-
 * keyframe `gnome-card-reveal-flash` (gnome-card.css) — плеер лишь НАВЕШИВАЕТ класс
 * `gnome-card--revealing` на время фазы и снимает по завершении (attach-не-перерисовка,
 * §3). Settle (подмену силуэта на открытый рендер) делает карточка через `onSettled`.
 *
 * SCOPE: последовательное проигрывание reveal на списке слот-элементов + reduce-motion +
 * `onSettled(slot)` хук финала + cleanup класса/`will-change`. NOT IN SCOPE: вычисление
 * «что открылось» (экран, `lib/gnome-state.newlyUnlockedColors`); сам открытый рендер
 * карточки (`components/gnome-card.revealSlot/applyState`); server-grant (GN-4b).
 *
 * Cross-references:
 *   - lib/gnome-state.ts (`newlyUnlockedColors` — что reveal'ить; экран зовёт ПЕРЕД сюда).
 *   - components/gnome-card.ts (`revealSlot()` делегирует сюда; `applyState` = settle-рендер).
 *   - gnome-card.css (keyframe `gnome-card-reveal-flash` + класс `gnome-card--revealing`).
 *   - meta-gnomes.md §5.1 (reveal на плашке), §6 (очередь / прерванная анимация); тикет §O-8.
 */

/**
 * Длительность фазы вспышки одного reveal (ms). **§8-trace (NO-HARDCODE, §4):** из
 * §8-диапазона морфа карточки 150–200ms ease-out — тот же порядок, что hover-морф
 * `transition: height 140ms ease-out` (gnome-card.css). Единственная «ручка» тайминга
 * reveal на проект — меняется ТОЛЬКО здесь. Должна совпадать с длительностью CSS-keyframe
 * `gnome-card-reveal-flash`.
 */
export const REVEAL_FLASH_MS = 180;

/** CSS-класс анимирующегося слота (вешается на время фазы вспышки; CSS держит keyframe). */
export const REVEALING_CLASS = 'gnome-card--revealing';

/**
 * Минимальный контракт слот-элемента, нужный плееру. Реальный потребитель передаёт
 * `HTMLElement` карточки; для юнит-тестов достаточно `classList` (пометить «играю») +
 * `style` (transform-origin / will-change) — анимация-вид проверяется Playwright'ом.
 */
export interface RevealSlot {
  readonly classList: {
    add(token: string): void;
    remove(token: string): void;
    contains(token: string): boolean;
  };
  /**
   * Writable style-bag (transform-origin / will-change на время вспышки). Реальный
   * потребитель — `HTMLElement.style` (передаёт `root` с приведением, т.к.
   * `CSSStyleDeclaration` не имеет string-index-сигнатуры); фейк-слот теста —
   * `Record<string,string>`. Плеер пишет только два известных свойства.
   */
  readonly style: Record<string, string>;
}

/** Опции плеера reveal-очереди. */
export interface RevealGnomesOptions {
  /**
   * Учитывать prefers-reduced-motion (DI, как `reward-block`). `true` → мгновенный финал
   * каждого слота БЕЗ анимации и задержек очереди (AC-16). Экран вычисляет флаг из
   * matchMedia и прокидывает (а не модуль читает window — юнит-тестируемость).
   */
  readonly reduceMotion: boolean;
  /**
   * Хук «слот доведён до финала» — карточка подменяет силуэт на открытый рендер
   * (`applyState` открытого). Вызывается РОВНО раз на слот, после фазы вспышки (или
   * сразу при reduce-motion). Без него reveal всё равно резолвится (no-op-safe, AC-18).
   */
  readonly onSettled?: (slot: RevealSlot) => void;
}

/** Промис-задержка (только когда reduce-motion ВЫКЛ — фаза вспышки реальна). */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Проиграть reveal на списке слотов ПОСЛЕДОВАТЕЛЬНО (очередь, AC-17). Порядок = как
 * передан (экран сортирует по возрастанию цвета = индексу вехи, §5.1). Каждый слот:
 * вспышка (класс `gnome-card--revealing` на `REVEAL_FLASH_MS`, `transform-origin:center`,
 * `will-change:transform` — соседи не прыгают, AC-15) → `onSettled(slot)` (финал) →
 * следующий. reduce-motion → каждый сразу в финал без таймеров (Promise резолвится в тот
 * же тик, AC-16). Пустой список / no-op хук → резолвится без зависания (server-truth —
 * истина, пропущенный reveal не страшен, AC-18).
 *
 * @param slots — слот-элементы новооткрытых гномов (порядок = очередь reveal).
 * @param opts — reduce-motion (DI) + onSettled (финал-рендер карточки).
 */
export async function revealGnomes(
  slots: readonly RevealSlot[],
  opts: RevealGnomesOptions,
): Promise<void> {
  // Defensive: пустой / не-массив → мгновенный no-op (нет зависшего reveal, AC-18).
  if (!Array.isArray(slots) || slots.length === 0) return;

  for (const slot of slots) {
    if (opts.reduceMotion) {
      // reduce-motion: мгновенный финал, БЕЗ класса-анимации и БЕЗ таймера (AC-16).
      opts.onSettled?.(slot);
      continue;
    }

    // Фаза вспышки: класс держит CSS-keyframe (scale 1→1.6→1 от center, GPU-only).
    // will-change:transform — браузер кладёт слот на свой слой → соседи не пересчитывают
    // layout, не «прыгают» (AC-15 / §9.1 Firefox). transform-origin:center — иконка не
    // дрейфует (§8.4).
    slot.style['transformOrigin'] = 'center';
    slot.style['willChange'] = 'transform';
    slot.classList.add(REVEALING_CLASS);

    await delay(REVEAL_FLASH_MS);

    // Конец фазы: снять класс + will-change (не держать слой вечно — §9.1), финал-рендер.
    slot.classList.remove(REVEALING_CLASS);
    slot.style['willChange'] = '';
    opts.onSettled?.(slot);
  }
}
